// =============================================================================
// DatesPipelineBlock — Bloc dates structuré par statut pipeline (S2 2026-05-28)
// =============================================================================
// Affiche pour chaque statut canonique du pipeline TEF (9 statuts) la date
// d'entrée du dossier dans ce statut.
//
// Sources (priorité décroissante) :
//   1. dossier.dates_pipeline[statut]      → override manuel JJ
//   2. min(historique[].at) where to==statut → trace atomique
//                                              (set_dossier_statut())
//   3. "-"                                    → jamais passé par ce statut
//
// Édition : clic sur la date → input date inline. Persiste via
// PATCH /api/metier/dossiers/{id} body {dates_pipeline: {...}}.
//
// Source : feedback Stéphanie sur dossiers importés sans historique
// + besoin de backfill pour le pilotage agent (date_octroi manquant
// notamment, cf. CLAUDE.md P1).
// =============================================================================

import { useState, useMemo, useCallback } from "react";
import { Calendar, Edit3, Check, X, Loader2, Info } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { toast } from "sonner";
import { API, getAuthHeaders } from "./helpers";

// Pipeline TEF canonique (cf. CLAUDE.md). 2 phases : TEF interne + suivi ANAH.
const PIPELINE_STATUTS = [
  // Phase TEF interne
  { code: "dossier_cree",     label: "Dossier créé",       phase: "tef" },
  { code: "visite_planifiee", label: "Visite planifiée",   phase: "tef" },
  { code: "visite_faite",     label: "Visite faite",       phase: "tef" },
  { code: "pret_depot",       label: "Prêt dépôt",         phase: "tef" },
  // Phase suivi ANAH
  { code: "depose",           label: "Déposé ANAH",        phase: "anah" },
  { code: "instruction",      label: "En instruction",     phase: "anah" },
  { code: "octroi",           label: "Octroi accordé",     phase: "anah" },
  // T-372 (S-3) : posées par « Demande d'avance faite » / « Demande de solde faite » (champs date_demande_avance / date_demande_solde)
  { code: "demande_avance",   label: "Demande d'avance",   phase: "anah" },
  { code: "demande_solde",    label: "Demande de solde",   phase: "anah" },
  { code: "travaux",          label: "Travaux en cours",   phase: "anah" },
  { code: "cloture",          label: "Clôturé",            phase: "anah" },
];

// Statuts dont la date vit dans un champ du dossier (priorité : override manuel > ce champ > historique)
const CHAMP_DATE_PROPRE = { demande_avance: "date_demande_avance", demande_solde: "date_demande_solde" };

// Aliases legacy → code canonique (le statut DB peut differer)
const ALIASES = {
  "déposé": "depose",
  "dossier_depose": "depose",
  "aides_en_instruction": "instruction",
  "aides_validees": "octroi",
  "clôture": "cloture",
};

function normalizeStatut(s) {
  if (!s) return s;
  return ALIASES[s] || s;
}

function toIsoDate(v) {
  if (!v) return "";
  // accepte ISO complet (avec T) ou ISO court
  if (typeof v !== "string") {
    try { return new Date(v).toISOString().slice(0, 10); } catch { return ""; }
  }
  if (v.length >= 10) return v.slice(0, 10);
  return v;
}

function formatFR(iso) {
  if (!iso) return "—";
  const d = toIsoDate(iso);
  if (!d || d.length < 10) return "—";
  const [y, m, day] = d.split("-");
  return `${day}/${m}/${y}`;
}

export default function DatesPipelineBlock({ dossier, tenantId, readOnly = false, onUpdated }) {
  const dossierId = dossier?.id || dossier?._id;

  // Calcul des dates pour chaque statut : override > historique min > null
  const datesByStatut = useMemo(() => {
    const out = {};
    const overrides = dossier?.dates_pipeline || {};
    const hist = Array.isArray(dossier?.historique) ? dossier.historique : [];

    // Index historique par statut cible (normalise)
    const histByStatut = {};
    for (const ev of hist) {
      if (ev?.type !== "statut_change") continue;
      const to = normalizeStatut(ev.to);
      if (!to) continue;
      const at = ev.at;
      if (!at) continue;
      if (!histByStatut[to] || at < histByStatut[to]) {
        histByStatut[to] = at;
      }
    }

    for (const s of PIPELINE_STATUTS) {
      const override = overrides[s.code];
      const propre = CHAMP_DATE_PROPRE[s.code] ? dossier?.[CHAMP_DATE_PROPRE[s.code]] : null;
      const fromHist = propre || histByStatut[s.code];
      out[s.code] = {
        iso: toIsoDate(override || fromHist),
        source: override ? "manuel" : fromHist ? "auto" : "none",
      };
    }
    return out;
  }, [dossier]);

  const [editingCode, setEditingCode] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);

  const startEdit = useCallback((code) => {
    setEditingCode(code);
    setEditValue(datesByStatut[code]?.iso || "");
  }, [datesByStatut]);

  const cancelEdit = useCallback(() => {
    setEditingCode(null);
    setEditValue("");
  }, []);

  const saveEdit = useCallback(async () => {
    if (!editingCode) return;
    setSaving(true);
    try {
      const next = { ...(dossier?.dates_pipeline || {}) };
      if (editValue) {
        next[editingCode] = editValue;
      } else {
        delete next[editingCode];
      }
      const res = await fetch(`${API}/api/metier/dossiers/${dossierId}`, {
        method: "PATCH",
        headers: getAuthHeaders(tenantId, "application/json"),
        body: JSON.stringify({ dates_pipeline: next }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      toast.success("Date enregistrée");
      cancelEdit();
      onUpdated?.();
    } catch (e) {
      toast.error(`Échec : ${e.message || "réseau"}`);
    } finally {
      setSaving(false);
    }
  }, [API, dossierId, tenantId, editingCode, editValue, dossier, cancelEdit, onUpdated]);

  return (
    <div
      className="bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] rounded-lg p-4"
      data-testid="dates-pipeline-block"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-[var(--oh-success-text)]" />
          <h4 className="text-sm font-medium text-[var(--oh-text-secondary)] uppercase tracking-wider">
            Dates pipeline
          </h4>
        </div>
        {saving && <Loader2 className="w-4 h-4 animate-spin text-[var(--oh-success-text)]" />}
      </div>

      <div className="space-y-2">
        {PIPELINE_STATUTS.map((s, idx) => {
          const { iso, source } = datesByStatut[s.code] || {};
          const isEditing = editingCode === s.code;
          const isPhaseBreak = idx === 4; // entre pret_depot et depose

          return (
            <div key={s.code}>
              {isPhaseBreak && (
                <div className="my-2 border-t border-[var(--oh-border)]" />
              )}
              <div
                className="flex items-center gap-3 py-1.5 px-2 rounded hover:bg-[var(--oh-bg-elevated)] transition-colors"
                data-testid={`date-row-${s.code}`}
              >
                <div className="flex-shrink-0 w-1 h-6 rounded" style={{
                  backgroundColor:
                    source === "manuel" ? "rgb(217, 119, 6)" :  // ambre
                    source === "auto"   ? "rgb(20, 184, 166)" : // teal
                                          "rgb(71, 85, 105)",   // slate
                }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[var(--oh-text-secondary)]">{s.label}</span>
                    {source === "manuel" && (
                      <span className="text-[10px] uppercase tracking-wider text-[var(--oh-info-text)]">
                        manuel
                      </span>
                    )}
                  </div>
                </div>
                {isEditing ? (
                  <div className="flex items-center gap-1">
                    <Input
                      type="date"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      className="h-7 w-36 bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] text-xs font-mono"
                      autoFocus
                      data-testid={`date-input-${s.code}`}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-7 px-1.5"
                      onClick={saveEdit}
                      disabled={saving}
                      data-testid={`date-save-${s.code}`}
                      aria-label={`Enregistrer date ${s.label}`}
                    >
                      <Check className="w-3.5 h-3.5 text-[var(--oh-success-text)]" />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-7 px-1.5"
                      onClick={cancelEdit}
                      disabled={saving}
                      aria-label="Annuler"
                    >
                      <X className="w-3.5 h-3.5 text-[var(--oh-text-secondary)]" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-sm font-mono ${iso ? "text-[var(--oh-text-secondary)]" : "text-[var(--oh-text-muted)]"}`}
                      data-testid={`date-value-${s.code}`}
                    >
                      {formatFR(iso)}
                    </span>
                    {!readOnly && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-7 px-1.5 opacity-60 hover:opacity-100"
                        onClick={() => startEdit(s.code)}
                        aria-label={`Modifier date ${s.label}`}
                        data-testid={`date-edit-${s.code}`}
                      >
                        <Edit3 className="w-3.5 h-3.5 text-[var(--oh-text-secondary)]" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-[var(--oh-text-muted)] mt-3 flex items-start gap-1.5">
        <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
        <span>
          Auto = trace historique transitions. Manuel = date saisie par un opérateur
          (backfill dossiers importés). L'override manuel prime sur l'auto.
        </span>
      </p>
    </div>
  );
}
